import { useCallback, useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import { EmptyPanel } from '@/components/px/EmptyPanel'
import { ErrorPanel } from '@/components/px/ErrorPanel'
import { LoadingPanel } from '@/components/px/LoadingPanel'
import { SectionLabel } from '@/components/px/SectionLabel'
import { selectActiveOrgId } from '@/features/auth/slices/sessionSlice'
import { asApiError } from '@/services/apiError'
import { cn } from '@/lib/utils'
import { useAppDispatch, useAppSelector } from '@/store'
import {
  loadArtefact,
  selectArtefactContent,
  selectArtefactError,
  selectArtefactLoading,
  selectArtefactMissing,
  selectBoardProjectId,
  selectSelectedTask
} from '../slices/boardSlice'
import { ARTEFACT_KIND_SUFFIXES, type ArtefactKindSuffix, type BoardTaskRow } from '../types'

/**
 * The `*Files` array (deferred finding c1-9) the selected `kind` reads from —
 * an empty array means the server has nothing for this task/kind, so the
 * "No artefact yet" state is already derivable client-side; dispatching
 * `loadArtefact` anyway is a guaranteed 404 plus a console error per
 * selection.
 */
const filesFor = (task: BoardTaskRow, kind: ArtefactKindSuffix): string[] => {
  switch (kind) {
    case 'spec':
      return task.specFiles ?? []
    case 'plan':
      return task.planFiles ?? []
    case 'code-changes':
      return task.codeChangesFiles ?? []
    case 'tests':
      return task.testFiles ?? []
  }
}

/**
 * The artefact under review (AC-2 / AC-3). `kind` sends one of the four
 * `*-content` path segments and never a bare kind (Decision 5); the
 * response is markdown, rendered as preformatted text — never parsed as
 * JSON. A 404 renders this panel's OWN empty state, never the page's
 * blocked state (INV-4.4).
 */
export function ReviewCard() {
  const { t } = useTranslation('projectWorkspace')
  const dispatch = useAppDispatch()
  const orgId = useAppSelector(selectActiveOrgId)
  const { projectId: routeProjectId } = useParams<{ projectId: string }>()
  const boardProjectId = useAppSelector(selectBoardProjectId)
  // The route wins when it names a project (fix cycle 1, GRP-82297E9A): the
  // board slice survives the overview, so on a deep link to
  // `/project/:projectId/build` it can still hold the PREVIOUS project on
  // first paint. `/my/board` and `/my/project` carry no `:projectId` segment
  // (finding 2, GRP-B3D9F416) — there the board's loaded project is used.
  const projectId = routeProjectId ?? boardProjectId ?? undefined
  const task = useAppSelector(selectSelectedTask)
  const artefact = useAppSelector(selectArtefactContent)
  const loading = useAppSelector(selectArtefactLoading)
  const missing = useAppSelector(selectArtefactMissing)
  const artefactError = useAppSelector(selectArtefactError)
  const [kind, setKind] = useState<ArtefactKindSuffix>('spec')
  // Adjusting state DURING render (React's own pattern for "reset on prop
  // change") rather than in an effect — a `setState` synchronously inside a
  // `useEffect` body triggers a second cascading render for no benefit here.
  const [kindTaskId, setKindTaskId] = useState<string | null>(null)
  const selectedTaskId = task?.legacyId ?? null
  if (selectedTaskId !== kindTaskId) {
    setKindTaskId(selectedTaskId)
    setKind('spec')
  }

  // Deferred finding c1-9: `hasFiles` is a SCALAR derived from the task, kept
  // out of the effect's own deps as an object — depending on `task` itself
  // (fix wave 1, finding 7 / deferred finding c1-7) re-requested the artefact
  // on every realtime refetch that replaced the task object with an
  // equal-content one.
  const hasFiles = task !== null && filesFor(task, kind).length > 0

  useEffect(() => {
    if (orgId === null || projectId === undefined || selectedTaskId === null || !hasFiles) return
    void dispatch(loadArtefact({ orgId, projectId, taskId: selectedTaskId, kind }))
  }, [dispatch, orgId, projectId, selectedTaskId, kind, hasFiles])

  const onRetryClicked = useCallback(() => {
    if (orgId === null || projectId === undefined || selectedTaskId === null) return
    void dispatch(loadArtefact({ orgId, projectId, taskId: selectedTaskId, kind }))
  }, [dispatch, orgId, projectId, selectedTaskId, kind])

  if (task === null) {
    return (
      <section aria-label={t('board.review.title')} data-testid="review-card">
        <SectionLabel>{t('board.review.title')}</SectionLabel>
        <div className="mt-2">
          <EmptyPanel title={t('board.review.noSelectionTitle')} body={t('board.review.noSelectionBody')} />
        </div>
      </section>
    )
  }

  return (
    <section
      aria-label={t('board.review.title')}
      className="rounded-lg border border-mint bg-accent px-5 py-5"
      data-testid="review-card"
    >
      <SectionLabel>{t('board.review.title')}</SectionLabel>
      <h2 className="mt-1 font-mono text-lg font-semibold text-foreground">{task.title}</h2>
      <p className="text-xs text-muted-foreground">{task.legacyId}</p>

      <div className="mt-3 flex flex-wrap gap-1.5" role="tablist" aria-label={t('board.review.kindLabel')}>
        {ARTEFACT_KIND_SUFFIXES.map((candidate) => (
          <button
            key={candidate}
            type="button"
            role="tab"
            aria-selected={candidate === kind}
            onClick={() => setKind(candidate)}
            data-testid={`review-kind-${candidate}`}
            className={cn(
              'rounded-full px-3 py-1 text-xs font-medium',
              candidate === kind
                ? 'bg-primary text-primary-foreground'
                : 'border border-input bg-card text-ink-soft hover:bg-muted'
            )}
          >
            {t(`board.artefactKind.${candidate}`)}
          </button>
        ))}
      </div>

      <div className="mt-3 rounded-lg border border-border bg-card">
        {loading ? (
          <LoadingPanel label={t('board.review.loading')} />
        ) : artefactError !== null ? (
          // Deferred findings c1-4/c2-2: a failed read replaces "loading"
          // with an in-pane error — the two are now mutually exclusive
          // branches, never rendered at once.
          <ErrorPanel
            error={asApiError({ status: artefactError.status, key: artefactError.code, message: artefactError.message })}
            onRetryClicked={onRetryClicked}
          />
        ) : !hasFiles || missing ? (
          <EmptyPanel title={t('board.review.emptyTitle')} body={t('board.review.emptyBody')} />
        ) : artefact !== null && artefact.kind === kind ? (
          <pre
            data-testid="review-artefact-markdown"
            className="max-h-[28rem] overflow-auto whitespace-pre-wrap px-4 py-3 text-sm text-foreground"
          >
            {artefact.markdown}
          </pre>
        ) : (
          <LoadingPanel label={t('board.review.loading')} />
        )}
      </div>
    </section>
  )
}
